import { FetcherOptions, FetcherResponse } from '../../../components/Hooks/useList.types';
import LangEnum from '../../../lang/LangEnum';
import { ErrorResponse } from '../../../lib/basicRepository';
import { Catalog, CatalogSectionCount } from '../../catalogs/catalog.types';
import { ESProductCatalogSearch } from '../../catalogs/products/product-catalog-es.types';
import { onlineCatalogsRepository } from '../../catalogs/repositories/onlineCatalogs.repository';
import {
  GetCatalogSectionsOfflineOutput,
  GetSellInPriceRangeOutput,
  OfflineSectionProductSet,
} from '../../catalogs/repositories/types/catalogs.repository.types';
import { offlineCatalogsRepository } from '../repositories/offlineCatalogs.repository';
import { OfflineCatalogTransformers } from '../transformers/OfflineDatabaseCatalog.transformers';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import GetCatalogSectionsUtils from './utils/getCatalogSections.utils';
import GetCatalogItemSearchUtils from './utils/getESProductCatalogSearch.utils';
import { notImplementedInOfflineError } from './utils/repository.offlineAdapters.utils';

export const onlineCatalogsRepositoryOfflineAdapters = (
  id: string,
): ReturnType<typeof onlineCatalogsRepository> => {
  return {
    create: () => notImplementedInOfflineError('create'),
    update: () => notImplementedInOfflineError('update'),
    remove: () => notImplementedInOfflineError('remove'),
    addProducts: () => notImplementedInOfflineError('addProducts'),
    moveProductsToSection: () => notImplementedInOfflineError('moveProductsToSection'),
    share: () => notImplementedInOfflineError('share'),
    syncDynamicCatalog: () => notImplementedInOfflineError('syncDynamicCatalog'),
    getSimplifiedProducts: () => notImplementedInOfflineError('getSimplifiedProducts'),
    addKitsToCatalog: () => notImplementedInOfflineError('addKitsToCatalog'),
    updateCatalog: () => notImplementedInOfflineError('updateCatalog'),
    updateNavigationPreferences: () => notImplementedInOfflineError('updateNavigationPreferences'),
    updateViewPreferences: () => notImplementedInOfflineError('updateViewPreferences'),
    batchUpdateCatalogSections: () => notImplementedInOfflineError('batchUpdateCatalogSections'),

    getOne: async () => {
      const offlineCatalog = await offlineCatalogsRepository.getById(id);

      if (!offlineCatalog) {
        return createApiSauceResponse({
          ok: false,
          data: {
            message: 'catálogo não encontrado',
            code: LangEnum.GLOBAL_CATALOG_NOT_FOUND,
          },
        });
      }

      return createApiSauceResponse<Catalog, {}>({
        ok: true,
        data: OfflineCatalogTransformers.toOnlineCatalog(offlineCatalog),
      });
    },

    getCatalogSections: async (params): Promise<GetCatalogSectionsOfflineOutput> => {
      const offlineCatalog = await offlineCatalogsRepository.getById(id);

      if (!offlineCatalog) {
        return {
          offlineSectionProductSet: new Set<string>(),
          sectionsResponse: createApiSauceResponse<CatalogSectionCount[]>({
            ok: false,
            data: [],
          }),
        };
      }

      const { sections, offlineSectionProductSet } =
        await GetCatalogSectionsUtils.getSectionsWithAvailableProducts(
          offlineCatalog.sections,
          params,
        );

      return {
        offlineSectionProductSet,
        sectionsResponse: createApiSauceResponse<CatalogSectionCount[]>({
          ok: true,
          data: await OfflineCatalogTransformers.toOnlineCatalogSections(sections),
        }),
      };
    },

    getSellInPriceRange: async (params: { orderId?: string; priceTableId?: string }) => {
      const offlineCatalog = await offlineCatalogsRepository.getById(id);

      if (!offlineCatalog) {
        return createApiSauceResponse<GetSellInPriceRangeOutput, ErrorResponse>({
          ok: false,
          data: {
            message: 'catálogo não encontrado',
            code: LangEnum.GLOBAL_CATALOG_NOT_FOUND,
          },
        });
      }

      const priceTableId = params.priceTableId;
      const range = priceTableId
        ? offlineCatalog.filterOptionsMetadata.sellInPriceByPriceTable?.[priceTableId]
        : undefined;

      return createApiSauceResponse<GetSellInPriceRangeOutput, ErrorResponse>({
        ok: true,
        data: range ?? { min: 0, max: 1000000 },
      });
    },

    getESProductCatalogSearch: async (
      options: FetcherOptions,
      offlineSectionProductSet: OfflineSectionProductSet,
    ): Promise<FetcherResponse<ESProductCatalogSearch>> => {
      const offlineCatalog = await offlineCatalogsRepository.getById(id);

      if (!offlineCatalog) {
        return {
          tag: 'error',
          error: 'catálogo não encontrado',
        };
      }

      const paginated = await GetCatalogItemSearchUtils.paginateValidCatalogSectionProducts({
        options,
        offlineCatalog,
        offlineSectionProductSet,
      });

      const transformed = await OfflineCatalogTransformers.mapCatalogSectionItemToEntity(paginated);

      return {
        tag: 'success',
        data: transformed,
        count: transformed.length,
      };
    },
  };
};
